import * as React from 'react';

import { CircularProgress, Collapse, Typography } from '@mui/material';
import {
  ASK_CLARIFYING_QUESTIONS_TOOL,
  CHECK_DOCUMENT_TEMPLATE_LAYOUT_TOOL,
  COMPOSE_EMAIL_TOOL,
} from '@realadvisor/common-types';
import { parse, print } from 'graphql';
import {
  BookOpen,
  Check,
  ChevronRight,
  CircleAlert,
  Compass,
  Database,
  FileInput,
  FilePen,
  FileSearch,
  FileText,
  FolderOpen,
  Globe,
  Images,
  type LucideIcon,
  Mail,
  MailSearch,
  Network,
  Palette,
  Pencil,
  Search,
  ShieldQuestion,
  Sparkles,
  SquareTerminal,
  Stethoscope,
  WandSparkles,
  Wrench,
  X,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

import {
  ADD_PROPERTY_IMAGES_TOOL_NAME,
  AddPropertyImagesToolCard,
} from './AddPropertyImagesToolCard';
import {
  ApprovalActions,
  type ApprovalResponseHandler,
  type Status,
  type ToolPart,
  statusOf,
} from './ApprovalActions';
import {
  SAVE_ARTIFACT_TOOL_NAME,
  SHOW_FILES_TOOL_NAME,
  saveArtifactErrorOf,
  savedArtifactOf,
  shownFilesOf,
} from './artifactFiles';
import {
  type ThemeDocumentKind,
  themeDocumentKindOf,
  themeSwitchLinkOf,
} from './chatRecordRefresh';
import { ClarifyingQuestionsCard } from './ClarifyingQuestionsCard';
import {
  type ComposeEmailResultHandler,
  composeEmailStepSummary,
} from './composeEmailCard';
import { ComposeEmailToolCard } from './ComposeEmailToolCard';
import { SHOW_CRM_IMAGES_TOOL_NAME, shownCrmImagesOf } from './crmImages';
import { rootFieldsOf } from './graphqlRootFields';
import {
  sandboxFailureOf,
  sandboxFailureText,
  withoutSandboxFileContent,
} from './sandboxToolPresenters';
import { SavedFileCard } from './SavedFileCard';
import { ShowCrmImagesCard } from './ShowCrmImagesCard';
import { ShowFilesCard } from './ShowFilesCard';
import { StreamingMarkdown } from './StreamingMarkdown';
import { layoutCheckFailed } from './templateLayoutCheck';
import { ReadableBlocks, usePresenterLabels } from './ToolCallReadable';
import {
  isExploreSchemaTool,
  isMutationTool,
  isQueryTool,
  isTypedDataTool,
  mutationActionsOf,
  presentTool,
  refusalProblemsOf,
  showsRawInputResult,
} from './toolPresenters';
import { VisualToolCard } from './VisualToolCard';
import {
  GENERATE_IMAGE_TOOL_NAME,
  RENDER_VISUAL_TOOL_NAME,
  isVisualToolName,
  visualToolPhaseOf,
} from './visualToolOutput';

// Vercel AI Elements `<Tool>`-style display, built with MUI to match the CRM.
// Renders a collapsible card per tool call: header with a humanised label and
// status, expandable to show the raw input/output. Tools flagged with
// needsApproval (graphql_mutation) pause in `approval-requested` and render
// approve/deny buttons that resume the agent loop.
//
// ToolPart / Status / statusOf / ApprovalActions live in ApprovalActions.tsx —
// specialised cards import them from there, never from this file, so the
// specialised-card imports below stay acyclic.

export const TOOL_ICONS: Record<string, LucideIcon> = {
  search_docs: Search,
  read_doc: BookOpen,
  graphql_explore_schema: Database,
  graphql_query: Database,
  graphql_mutation: Pencil,
  navigate_to: Compass,
  load_skill: Sparkles,
  web_search: Globe,
  fetch_url: Globe,
  dns_lookup: Network,
  mail_settings_detect: MailSearch,
  mail_server_probe: Stethoscope,
  [COMPOSE_EMAIL_TOOL]: Mail,
  [RENDER_VISUAL_TOOL_NAME]: Palette,
  [GENERATE_IMAGE_TOOL_NAME]: WandSparkles,
  [SAVE_ARTIFACT_TOOL_NAME]: FileText,
  list_artifacts: FolderOpen,
  read_artifact: FileText,
  [SHOW_FILES_TOOL_NAME]: Images,
  [SHOW_CRM_IMAGES_TOOL_NAME]: Images,
  sandbox_exec: SquareTerminal,
  sandbox_write_file: FilePen,
  sandbox_read_file: FileSearch,
  load_artifact: FileInput,
};

// Fallback label for tools not in the curated set (rare). Dynamic, so it can't
// go through t(); the curated labels below are localized.
const humanize = (toolName: string): string =>
  toolName
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();

const stringify = (value: unknown): string => {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

// A renderable chunk of a tool's input/output. `code` runs through Shiki
// highlighting (Streamdown), `markdown` is interpreted as markdown, and `text`
// is shown verbatim in a <pre> (used for raw error/denied messages so the full
// detail stays screenshot-friendly).
export type Segment =
  | { kind: 'code'; lang: 'graphql' | 'json'; text: string }
  | { kind: 'markdown'; text: string }
  | { kind: 'text'; text: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value != null;

// Every crm-chat-api tool asks the agent for a `title` (see
// crm-chat-api/tools/step-title.ts) used as the step label. Models can still
// omit it or leave it empty, and while the input streams it may be partial.
export const stepTitleOf = (part: ToolPart): string | null => {
  if (!isRecord(part.input)) {
    return null;
  }
  const title = part.input.title;
  return typeof title === 'string' && title.trim().length > 0
    ? title.trim()
    : null;
};

// The GraphQL query/mutation tools take `{ query | mutation, variables }`.
// Pull the operation out of the JSON so its real newlines survive (JSON.stringify
// would escape them to literal "\n") and highlight it as GraphQL; show variables
// as a separate JSON block.
const isGraphQLOperationTool = (toolName: string): boolean =>
  isQueryTool(toolName) || isMutationTool(toolName);

// Agents often send the whole operation on one line; reformat it so it reads
// as a document. Unparsable (e.g. still streaming) text is shown as is.
const formatGraphQL = (source: string): string => {
  try {
    return print(parse(source));
  } catch {
    return source.trim();
  }
};

export const buildInputSegments = (
  part: ToolPart,
  // The live chat already shows the title as the step label; the admin
  // inspector keeps it in the raw input.
  { hideTitle = false }: { hideTitle?: boolean } = {},
): Segment[] => {
  const { toolName } = part;
  const rawInput = withoutSandboxFileContent(toolName, part.input);
  const input =
    hideTitle && isRecord(rawInput)
      ? Object.fromEntries(
          Object.entries(rawInput).filter(([key]) => key !== 'title'),
        )
      : rawInput;
  if (isGraphQLOperationTool(toolName) && isRecord(input)) {
    const operation = input.query ?? input.mutation;
    const segments: Segment[] = [];
    if (typeof operation === 'string' && operation.trim().length > 0) {
      segments.push({
        kind: 'code',
        lang: 'graphql',
        text: formatGraphQL(operation),
      });
    }
    if (isRecord(input.variables) && Object.keys(input.variables).length > 0) {
      segments.push({
        kind: 'code',
        lang: 'json',
        text: JSON.stringify(input.variables, null, 2),
      });
    }
    if (segments.length > 0) {
      return segments;
    }
  }
  if (hideTitle && isRecord(input) && Object.keys(input).length === 0) {
    return [];
  }
  const text = stringify(input);
  return text.length > 0 ? [{ kind: 'code', lang: 'json', text }] : [];
};

export const buildOutputSegments = (
  part: ToolPart,
  labels: { failed: string; denied: string; refused: string },
  // The admin inspector keeps the full reason the model was given.
  { fullRefusalReason = false }: { fullRefusalReason?: boolean } = {},
): Segment[] => {
  const status = statusOf(part);
  if (status === 'error') {
    return [{ kind: 'text', text: part.errorText ?? labels.failed }];
  }
  // A user denial has no reason; a reason means the server refused the call
  // before asking the user.
  if (status === 'denied') {
    const reason = part.approval?.reason;
    return [
      {
        kind: 'text',
        text: reason
          ? `${labels.refused}\n\n${
              fullRefusalReason ? reason : refusalProblemsOf(reason)
            }`
          : labels.denied,
      },
    ];
  }
  const text = stringify(withoutSandboxFileContent(part.toolName, part.output));
  if (text.length === 0) {
    return [];
  }
  // Schema exploration returns "## heading" + "  name: Type" lines: as markdown
  // the 2-space indents collapse into one paragraph, so keep it verbatim.
  if (isExploreSchemaTool(part.toolName)) {
    return [{ kind: 'text', text }];
  }
  if (part.toolName === 'load_skill') {
    return [{ kind: 'markdown', text }];
  }
  // Query/mutation results are JSON.stringify'd server-side; any other tool's
  // string output is shown as-is (json fence is harmless for plain text).
  return [{ kind: 'code', lang: 'json', text }];
};

const ChatToolCallInner = ({
  part,
  onApprovalResponse,
  isLastAssistant = false,
  isLoading = false,
  onComposeEmailResult,
  isMobile = false,
}: {
  part: ToolPart;
  onApprovalResponse?: ApprovalResponseHandler;
  // Whether the part belongs to the chat's last turn, and whether that turn is
  // still streaming: the clarifying questions card shows "waiting" only then.
  isLastAssistant?: boolean;
  isLoading?: boolean;
  onComposeEmailResult?: ComposeEmailResultHandler;
  isMobile?: boolean;
}) => {
  if (part.toolName === ASK_CLARIFYING_QUESTIONS_TOOL) {
    return (
      <ClarifyingQuestionsCard
        part={part}
        isLastAssistant={isLastAssistant}
        isLoading={isLoading}
      />
    );
  }
  if (part.toolName === COMPOSE_EMAIL_TOOL) {
    return (
      <ComposeEmailToolCard
        part={part}
        onResult={onComposeEmailResult}
        isMobile={isMobile}
      />
    );
  }
  // Specialised card with photo thumbnails. Delete this branch (and the
  // component file) to fall back to the generic card below.
  if (part.toolName === ADD_PROPERTY_IMAGES_TOOL_NAME) {
    return (
      <AddPropertyImagesToolCard
        part={part}
        onApprovalResponse={onApprovalResponse}
      />
    );
  }
  if (isVisualToolName(part.toolName)) {
    return <VisualToolCard part={part} />;
  }
  const savedArtifact = savedArtifactOf(part);
  if (savedArtifact != null) {
    return <SavedFileCard artifact={savedArtifact} />;
  }
  const shownFiles = shownFilesOf(part);
  if (shownFiles != null) {
    return <ShowFilesCard shown={shownFiles} />;
  }
  const shownCrmImages = shownCrmImagesOf(part);
  if (shownCrmImages != null) {
    return <ShowCrmImagesCard shown={shownCrmImages} />;
  }
  return (
    <GenericToolCall part={part} onApprovalResponse={onApprovalResponse} />
  );
};

export const toolIconOf = (toolName: string): LucideIcon =>
  TOOL_ICONS[toolName] ?? Wrench;

const MAX_LABEL_FIELDS = 3;
const MAX_COMMAND_DETAIL_CHARS = 60;

// What a titleless step touched, read from its input: the queried tables, the
// explored schema path, the searched text, the opened path, the doc slug.
const inputDetailOf = (part: ToolPart): string | null => {
  const input = part.input;
  if (!isRecord(input)) {
    return null;
  }
  const str = (value: unknown): string | null =>
    typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
  if (isGraphQLOperationTool(part.toolName)) {
    const operation = str(input.query) ?? str(input.mutation);
    const fields = operation != null ? rootFieldsOf(operation) : [];
    if (fields.length === 0) {
      return null;
    }
    const shown = fields.slice(0, MAX_LABEL_FIELDS).join(', ');
    return fields.length > MAX_LABEL_FIELDS ? `${shown}, …` : shown;
  }
  if (isExploreSchemaTool(part.toolName)) {
    return str(input.path);
  }
  if (isTypedDataTool(part.toolName)) {
    return str(input.object_type);
  }
  if (part.toolName === 'search_docs') {
    const query = str(input.query);
    return query != null ? `"${query}"` : null;
  }
  if (part.toolName === 'read_doc') {
    return str(input.slug);
  }
  if (part.toolName === 'navigate_to') {
    return str(input.path);
  }
  if (part.toolName === 'load_skill' || part.toolName === 'save_artifact') {
    return str(input.name);
  }
  if (part.toolName === 'mail_settings_detect') {
    return str(input.emailOrDomain);
  }
  if (part.toolName === 'mail_server_probe') {
    const host = str(input.host);
    return host != null && typeof input.port === 'number'
      ? `${host}:${input.port}`
      : host;
  }
  if (part.toolName === 'sandbox_exec') {
    const command = str(input.command);
    const firstLine = command?.split('\n')[0] ?? null;
    return firstLine != null && firstLine.length > MAX_COMMAND_DETAIL_CHARS
      ? `${firstLine.slice(0, MAX_COMMAND_DETAIL_CHARS)}…`
      : firstLine;
  }
  if (
    part.toolName === 'sandbox_write_file' ||
    part.toolName === 'sandbox_read_file' ||
    part.toolName === 'load_artifact'
  ) {
    return str(input.path);
  }
  return null;
};

// Row label for a tool call: the agent-written title when present, else the
// curated static label plus what the input touched ("Queried CRM data: lots,
// leads"). Returns null while the input is still streaming without a title, so
// the row shows a placeholder instead of flashing the static label first.
export const useToolLabel = (part: ToolPart): string | null => {
  const { t } = useLocale();
  const status = statusOf(part);
  if (part.toolName === COMPOSE_EMAIL_TOOL) {
    const summary = composeEmailStepSummary(part);
    switch (summary.kind) {
      case 'sent':
        return summary.subject !== ''
          ? t('Email sent: {{subject}}', { subject: summary.subject })
          : t('Email sent');
      case 'mass-sent':
        return t('Mass email sent to {{count}} recipients', {
          count: summary.count,
        });
      case 'discarded':
        return t('Email discarded');
      case 'not-sent':
        return t('Email not sent');
    }
  }
  const title = stepTitleOf(part);
  if (title != null) {
    return title;
  }
  if (part.state === 'input-streaming') {
    return null;
  }
  if (status === 'approval-requested' && isMutationTool(part.toolName)) {
    return t('Wants to modify CRM data');
  }
  // Literal t() calls so the translator-scanner can extract these keys.
  const toolLabels: Record<string, string> = {
    search_docs: t('Searched documentation'),
    read_doc: t('Read documentation'),
    get_crm_capabilities: t('Looked at the CRM data'),
    query_crm_data: t('Queried CRM data'),
    get_crm_object: t('Opened a CRM record'),
    graphql_explore_schema: t('Explored the CRM data schema'),
    graphql_query: t('Queried CRM data'),
    graphql_mutation: t('Modified CRM data'),
    get_market_data_capabilities: t('Looked at the market data'),
    query_market_data: t('Queried market data'),
    get_market_data_object: t('Opened a market data record'),
    market_data_explore_schema: t('Explored the market data schema'),
    market_data_query: t('Queried market data'),
    // Names these two tools had in conversations recorded before the rename.
    scrapers_explore_schema: t('Explored the market data schema'),
    scrapers_query: t('Queried market data'),
    navigate_to: t('Opened a page'),
    read_current_page: t('Read the page on screen'),
    web_search: t('Searched the web'),
    fetch_url: t('Read a web page'),
    dns_lookup: t('Looked up DNS records'),
    mail_settings_detect: t('Detected mail settings'),
    mail_server_probe: t('Checked a mail server'),
    get_document_template: t('Read a document template'),
    create_team_template: t('Created a team template'),
    update_document_template: t('Updated a document template'),
    undo_document_template_change: t('Undid a template change'),
    [CHECK_DOCUMENT_TEMPLATE_LAYOUT_TOOL]: t('Checked the template pages'),
    look_at_report_pages: t('Looked at the report pages'),
    [RENDER_VISUAL_TOOL_NAME]: t('Created a visual'),
    [GENERATE_IMAGE_TOOL_NAME]: t('Generated an image'),
    [SAVE_ARTIFACT_TOOL_NAME]: t('Saved a file'),
    list_artifacts: t('Listed your files'),
    read_artifact: t('Read a file'),
    [SHOW_FILES_TOOL_NAME]: t('Showed your files'),
    [SHOW_CRM_IMAGES_TOOL_NAME]: t('Showed CRM photos'),
    sandbox_exec: t('Ran a command'),
    sandbox_write_file: t('Wrote a file in the sandbox'),
    sandbox_read_file: t('Read a file in the sandbox'),
    load_artifact: t('Loaded a file into the sandbox'),
    load_document_theme: t("Loaded the agency's document theme"),
    preview_document: t('Reviewed the document visually'),
  };
  const baseLabel = toolLabels[part.toolName] ?? humanize(part.toolName);
  const detail = inputDetailOf(part);
  return detail != null ? `${baseLabel}: ${detail}` : baseLabel;
};

const OTHER_THEME_KIND: Record<ThemeDocumentKind, ThemeDocumentKind> = {
  cma_report: 'lot_brochure',
  lot_brochure: 'cma_report',
};

const THEME_TOOLS = new Set([
  'get_document_theme',
  'preview_team_theme',
  'save_team_theme',
  'delete_team_theme',
]);

const TEMPLATE_TOOLS = new Set([
  'get_document_template',
  'create_team_template',
  'update_document_template',
  'undo_document_template_change',
  'look_at_report_pages',
]);

const isRefusedDocumentToolCall = (part: ToolPart): boolean => {
  if (statusOf(part) !== 'done') {
    return false;
  }
  if (part.toolName === CHECK_DOCUMENT_TEMPLATE_LAYOUT_TOOL) {
    return layoutCheckFailed(part.output);
  }
  return (
    (THEME_TOOLS.has(part.toolName) || TEMPLATE_TOOLS.has(part.toolName)) &&
    isRecord(part.output) &&
    part.output.ok === false
  );
};

export const ThemeSwitchLink = ({ part }: { part: ToolPart }) => {
  const { t } = useLocale();
  const link =
    part.toolName === 'preview_team_theme' && statusOf(part) === 'done'
      ? themeSwitchLinkOf(part.input, part.output)
      : null;
  if (link == null) {
    return null;
  }
  return (
    <Link
      to={link.path}
      css={{ display: 'inline-block', fontSize: '0.75rem', fontWeight: 500 }}
    >
      {link.kind === 'cma_report'
        ? t('Open a CMA report with the new look')
        : t('Open a brochure with the new look')}
    </Link>
  );
};

// What an approval-gated write does, derived from the mutation itself so the
// agent's title is never the only description the user approves against.
export const useWriteCaption = (part: ToolPart): string | null => {
  const { t } = useLocale();
  const labels = usePresenterLabels();
  const { pathname } = useLocation();
  if (part.toolName === 'preview_team_theme') {
    if (statusOf(part) !== 'approval-requested') {
      return null;
    }
    const themeInput = isRecord(part.input) ? part.input : {};
    const openKind = themeDocumentKindOf(pathname);
    const kind =
      themeInput.part === 'other' && openKind != null
        ? OTHER_THEME_KIND[openKind]
        : openKind;
    const count = Array.isArray(themeInput.applyTo)
      ? themeInput.applyTo.length
      : 0;
    if (themeInput.part === 'other' && count > 0) {
      return kind === 'cma_report'
        ? t('Puts this look on CMA reports: {{count}}', { count })
        : t('Puts this look on brochures: {{count}}', { count });
    }
    if (kind === 'cma_report') {
      return t('Any other CMA reports that use this theme will change too');
    }
    if (kind === 'lot_brochure') {
      return t('Any other brochures that use this theme will change too');
    }
    return t('Any other documents that use this theme will change too');
  }
  if (part.toolName === 'delete_team_theme') {
    const themeInput = isRecord(part.input) ? part.input : {};
    if (
      statusOf(part) !== 'approval-requested' ||
      typeof themeInput.themeName !== 'string' ||
      typeof themeInput.documentsOnTheme !== 'number'
    ) {
      return null;
    }
    return t(
      'Deletes the theme "{{name}}". Documents that use it go back to the default look: {{count}}',
      { name: themeInput.themeName, count: themeInput.documentsOnTheme },
    );
  }
  if (part.toolName === 'create_team_template') {
    const templateInput = isRecord(part.input) ? part.input : {};
    if (
      statusOf(part) !== 'approval-requested' ||
      typeof templateInput.name !== 'string' ||
      typeof templateInput.teamName !== 'string'
    ) {
      return null;
    }
    const name = templateInput.name.replace(/\s+/g, ' ').trim();
    const team = templateInput.teamName;
    if (themeDocumentKindOf(pathname) === 'lot_brochure') {
      return templateInput.makeTeamDefault === true
        ? t(
            'Creates the brochure template "{{name}}" for {{team}}, makes it the default for new brochures of this team and switches this brochure to it.',
            { name, team },
          )
        : t(
            'Creates the brochure template "{{name}}" for {{team}} and switches this brochure to it.',
            { name, team },
          );
    }
    return templateInput.makeTeamDefault === true
      ? t(
          'Creates the CMA template "{{name}}" for {{team}}, makes it the default for new CMA reports of this team and switches this report to it.',
          { name, team },
        )
      : t(
          'Creates the CMA template "{{name}}" for {{team}} and switches this report to it.',
          { name, team },
        );
  }
  if (!isMutationTool(part.toolName) || part.state === 'input-streaming') {
    return null;
  }
  const input = isRecord(part.input) ? part.input : {};
  const mutation = typeof input.mutation === 'string' ? input.mutation : null;
  const actions =
    mutationActionsOf(mutation, labels, input.variables) ??
    (mutation != null ? rootFieldsOf(mutation) : []);
  const detail = actions.join(', ');
  if (statusOf(part) === 'approval-requested') {
    const prefix = t('Wants to modify CRM data');
    return detail.length > 0 ? `${prefix}: ${detail}` : prefix;
  }
  return detail.length > 0 ? detail : null;
};

// The step row keeps the agent's title for a failed visual; the error itself
// is in the expanded Result.
export const useVisualFailureCaption = (part: ToolPart): string | null => {
  const { t } = useLocale();
  if (
    !isVisualToolName(part.toolName) ||
    visualToolPhaseOf(part) !== 'failed'
  ) {
    return null;
  }
  return part.toolName === GENERATE_IMAGE_TOOL_NAME
    ? t('Failed to generate the image')
    : t('Failed to create the visual');
};

// A sandbox call answered `{ error }` (e.g. the sandbox is busy): the step row
// says why, readable, while the step stays collapsed.
export const useSandboxFailureCaption = (part: ToolPart): string | null => {
  const labels = usePresenterLabels();
  const failure = sandboxFailureOf(part);
  return failure != null ? sandboxFailureText(failure, labels.sandbox) : null;
};

export const ToolStatusIcon = ({ status }: { status: Status }) => {
  const { colors } = useTheme();
  return status === 'running' || status === 'approval-responded' ? (
    <CircularProgress size={13} sx={{ color: colors.grey500 }} />
  ) : status === 'error' ? (
    <CircleAlert size={14} color={colors.errorText} />
  ) : status === 'approval-requested' ? (
    <ShieldQuestion size={14} color={colors.warningText} />
  ) : status === 'denied' ? (
    <X size={14} color={colors.errorText} />
  ) : (
    <Check size={14} color={colors.green600} />
  );
};

// A compose_email answered with "discarded" or "error", a visual that
// returned no image, or a save_artifact the store refused (quota, size, file
// type) is a successful tool result that still did nothing.
export const ToolPartStatusIcon = ({ part }: { part: ToolPart }) => {
  const { colors } = useTheme();
  if (
    (isVisualToolName(part.toolName) && visualToolPhaseOf(part) === 'failed') ||
    isRefusedDocumentToolCall(part) ||
    saveArtifactErrorOf(part) != null ||
    sandboxFailureOf(part) != null
  ) {
    return <CircleAlert size={14} color={colors.errorText} />;
  }
  if (part.toolName === COMPOSE_EMAIL_TOOL) {
    switch (composeEmailStepSummary(part).kind) {
      case 'discarded':
        return <X size={14} color={colors.grey500} />;
      case 'not-sent':
        return <CircleAlert size={14} color={colors.errorText} />;
      default:
        break;
    }
  }
  return <ToolStatusIcon status={statusOf(part)} />;
};

// Collapsed-by-default raw view (full query, variables, raw output) under the
// readable Input / Result sections. Never opens by itself: the user expands it.
const TechnicalDetails = ({
  segments,
  colors,
}: {
  segments: Segment[];
  colors: ReturnType<typeof useTheme>['colors'];
}) => {
  const { t } = useLocale();
  const [open, setOpen] = React.useState(false);
  return (
    <div css={{ marginTop: 8 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: 0,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          font: 'inherit',
          color: colors.grey500,
        }}
      >
        <ChevronRight
          size={12}
          style={{
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.15s ease',
            flexShrink: 0,
          }}
        />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {t('Technical details')}
        </Typography>
      </button>
      <Collapse in={open} unmountOnExit>
        <div css={{ paddingTop: 4 }}>
          {segments.map((segment, index) => (
            <SegmentView key={index} segment={segment} colors={colors} />
          ))}
        </div>
      </Collapse>
    </div>
  );
};

// Expanded body of a tool call: input, output/error, and the approval buttons
// while the agent loop waits on the user (unless the caller renders them
// outside the collapsible body: `showApproval` false). Tools with a presenter
// get readable Input / Result sections plus the raw view under "Technical
// details". web_search and fetch_url keep the raw JSON, with the same Input /
// Result captions. Any other tool shows the raw segments directly.
export const ToolCallDetails = ({
  part,
  onApprovalResponse,
  showApproval = true,
}: {
  part: ToolPart;
  onApprovalResponse?: ApprovalResponseHandler;
  showApproval?: boolean;
}) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  const labels = usePresenterLabels();
  const status = statusOf(part);
  const inputSegments = buildInputSegments(part, { hideTitle: true });
  const outputSegments = buildOutputSegments(part, {
    failed: t('Failed'),
    denied: t('Denied by user — the change was not executed'),
    refused: t('Refused automatically — not executed'),
  });
  const presentation = presentTool(part, labels);
  // Errors and denials always show their full raw text (never hidden behind
  // the toggle); otherwise a presenter's readable output replaces the raw one.
  const showRawOutput =
    presentation == null ||
    presentation.output === 'raw' ||
    status === 'error' ||
    status === 'denied';
  const readableOutput =
    !showRawOutput && presentation != null && presentation.output !== 'raw'
      ? presentation.output
      : [];
  const technicalSegments = [
    ...inputSegments,
    ...(showRawOutput ? [] : outputSegments),
  ];
  // Captions only. The segments stay the raw JSON; no readable presenter and
  // no Technical details drawer.
  const labeledRaw = presentation == null && showsRawInputResult(part.toolName);
  const outputTitle =
    status === 'error'
      ? t('Error')
      : presentation != null || labeledRaw
      ? t('Result')
      : t('Output');
  return (
    <div
      css={{
        padding: '0 10px 10px 10px',
        '& > :first-of-type': { marginTop: 0 },
      }}
    >
      {presentation == null
        ? inputSegments.length > 0 && (
            <ToolSection
              title={labeledRaw ? t('Input') : undefined}
              segments={inputSegments}
              colors={colors}
            />
          )
        : (presentation.input.length > 0 || !presentation.complete) && (
            <ToolSection title={t('Input')} colors={colors}>
              <ReadableBlocks blocks={presentation.input} colors={colors} />
              {!presentation.complete && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    marginTop: 0.5,
                    color: colors.grey500,
                  }}
                >
                  {t('Some details are only shown in Technical details')}
                </Typography>
              )}
            </ToolSection>
          )}
      {showRawOutput
        ? outputSegments.length > 0 && (
            <ToolSection
              title={outputTitle}
              segments={outputSegments}
              colors={colors}
            />
          )
        : readableOutput.length > 0 && (
            <ToolSection title={outputTitle} colors={colors}>
              <ReadableBlocks blocks={readableOutput} colors={colors} />
            </ToolSection>
          )}
      {presentation != null && technicalSegments.length > 0 && (
        <TechnicalDetails segments={technicalSegments} colors={colors} />
      )}
      {showApproval &&
        status === 'approval-requested' &&
        part.approval?.id != null &&
        onApprovalResponse != null && (
          <ApprovalActions
            approvalId={part.approval.id}
            onApprovalResponse={onApprovalResponse}
          />
        )}
    </div>
  );
};

const GenericToolCall = ({
  part,
  onApprovalResponse,
}: {
  part: ToolPart;
  onApprovalResponse?: ApprovalResponseHandler;
}) => {
  const { colors } = useTheme();
  const status = statusOf(part);
  // A pending approval must be visible without a click — the agent loop is
  // paused on the user's decision, so surface the mutation input by default.
  const [open, setOpen] = React.useState(status === 'approval-requested');
  React.useEffect(() => {
    if (status === 'approval-requested') {
      setOpen(true);
    }
  }, [status]);

  const label = useToolLabel(part);
  const Icon = toolIconOf(part.toolName);

  return (
    <div
      css={{
        border: `1px solid ${colors.divider}`,
        borderRadius: 8,
        backgroundColor: colors.white,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          padding: '7px 10px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          font: 'inherit',
          color: colors.grey700,
        }}
      >
        <ChevronRight
          size={14}
          color={colors.grey500}
          style={{
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.15s ease',
            flexShrink: 0,
          }}
        />
        <Icon size={15} color={colors.grey600} style={{ flexShrink: 0 }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, flex: 1, minWidth: 0 }}
          noWrap
        >
          {label ?? '…'}
        </Typography>
        <ToolStatusIcon status={status} />
      </button>
      <Collapse in={open} unmountOnExit>
        <ToolCallDetails part={part} onApprovalResponse={onApprovalResponse} />
      </Collapse>
    </div>
  );
};

const MAX_SEGMENT_CHARS = 4000;

const clamp = (text: string, maxChars: number): string =>
  text.length > maxChars ? `${text.slice(0, maxChars)}\n…` : text;

// The body container insets its children by SECTION_PADDING_X; code blocks
// cancel it with a negative margin so they bleed to the card edges.
const SECTION_PADDING_X = 10;

export const SegmentView = ({
  segment,
  colors,
  // The live chat clamps long segments to keep the conversation scannable;
  // the admin inspector passes Infinity to keep the full audit trail visible.
  maxChars = MAX_SEGMENT_CHARS,
}: {
  segment: Segment;
  colors: ReturnType<typeof useTheme>['colors'];
  maxChars?: number;
}) => {
  if (segment.kind === 'text') {
    return (
      <pre
        css={{
          margin: '4px 0 0',
          padding: '8px 10px',
          backgroundColor: colors.grey100,
          borderRadius: 6,
          fontSize: '0.75rem',
          lineHeight: 1.5,
          fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 220,
          overflow: 'auto',
        }}
      >
        {clamp(segment.text, maxChars)}
      </pre>
    );
  }

  if (segment.kind === 'markdown') {
    return (
      <div
        css={{
          marginTop: 4,
          maxHeight: 280,
          overflow: 'auto',
          '& > .crm-md > :first-of-type': { marginTop: 0 },
          '& > .crm-md > :last-of-type': { marginBottom: 0 },
        }}
      >
        <StreamingMarkdown content={clamp(segment.text, maxChars)} />
      </div>
    );
  }

  // Code blocks run edge-to-edge: cancel the body's horizontal padding and use
  // a 1px top divider (instead of the section caption) to separate the snippet
  // from the row above it. The Streamdown chrome (block margins, header
  // padding, square corners) is tuned via the `.crm-tool-code` rules in
  // tailwind.css — those overrides must live in the same unlayered stylesheet
  // as the global `.crm-md` rules to win the cascade (Emotion injects into a
  // layer, which unlayered rules always beat).
  return (
    <div
      className="crm-tool-code"
      css={{
        marginInline: -SECTION_PADDING_X,
        borderTop: `1px solid ${colors.divider}`,
        maxHeight: 280,
        overflow: 'auto',
      }}
    >
      <StreamingMarkdown
        content={`\`\`\`${segment.lang}\n${clamp(
          segment.text,
          maxChars,
        )}\n\`\`\``}
      />
    </div>
  );
};

const ToolSection = ({
  title,
  segments = [],
  colors,
  children,
}: {
  // Omitted for the raw input section — the row's title + the code-block
  // language header already say what it is.
  title?: string;
  segments?: Segment[];
  colors: ReturnType<typeof useTheme>['colors'];
  children?: React.ReactNode;
}) => (
  <div css={{ marginTop: title != null ? 8 : 0 }}>
    {title != null && (
      <Typography
        variant="caption"
        sx={{
          color: colors.grey500,
          fontWeight: 600,
          textTransform: 'uppercase',
        }}
      >
        {title}
      </Typography>
    )}
    {segments.map((segment, index) => (
      <SegmentView key={index} segment={segment} colors={colors} />
    ))}
    {children}
  </div>
);

export const ChatToolCall = React.memo(ChatToolCallInner);
